"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";

export default function PdfToImageTool() {
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState<"png" | "jpeg">("png");
  const [pages, setPages] = useState<{ index: number; url: string }[]>([]);
  const [processing, setProcessing] = useState(false);

  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setPages([]);
  }, []);

  const convert = useCallback(async () => {
    if (!file) return;
    setProcessing(true);

    try {
      const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
        import.meta.url
      ).toString();

      const data = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data }).promise;
      const results: { index: number; url: string }[] = [];

      for (let i = 0; i < pdf.numPages; i++) {
        const page = await pdf.getPage(i + 1);
        const viewport = page.getViewport({ scale: 2 }); // 2x for high res
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d")!;
        await page.render({ canvasContext: ctx, viewport, canvas }).promise;

        const mimeType = format === "png" ? "image/png" : "image/jpeg";
        const quality = format === "jpeg" ? 0.92 : undefined;
        const url = canvas.toDataURL(mimeType, quality);
        results.push({ index: i, url });
      }

      setPages(results);
      window.sfAnalytics?.track("tool_run", "Tool", "pdf-to-image");
    } catch {
      // Error rendering
    } finally {
      setProcessing(false);
    }
  }, [file, format]);

  const downloadPage = useCallback((pageIndex: number, url: string) => {
    const ext = format === "png" ? "png" : "jpg";
    const a = document.createElement("a");
    a.href = url;
    a.download = `page-${pageIndex + 1}.${ext}`;
    a.click();
  }, [format]);

  const downloadAll = useCallback(() => {
    pages.forEach((p) => downloadPage(p.index, p.url));
  }, [pages, downloadPage]);

  const reset = useCallback(() => {
    setFile(null);
    setPages([]);
  }, []);

  if (pages.length > 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">
            {pages.length} page{pages.length !== 1 ? "s" : ""} converted
          </h3>
          <div className="flex gap-3">
            <button onClick={downloadAll} className="btn-primary text-sm py-2">
              <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download all
            </button>
            <button onClick={reset} className="btn-secondary text-sm py-2">
              Convert another
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {pages.map((p) => (
            <div key={p.index} className="rounded-lg border border-slate-200 overflow-hidden group">
              <div className="aspect-[3/4] bg-slate-50 overflow-hidden">
                <img src={p.url} alt={`Page ${p.index + 1}`} className="w-full h-full object-contain" />
              </div>
              <div className="p-2 flex items-center justify-between bg-white border-t border-slate-100">
                <span className="text-xs text-slate-500 font-medium">Page {p.index + 1}</span>
                <button
                  onClick={() => downloadPage(p.index, p.url)}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FileUploader
        accept=".pdf"
        onFilesSelected={handleFilesSelected}
        label="Drop your PDF here"
        description="Convert each page to a high-quality image"
      />

      {file && (
        <>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Output format</label>
            <div className="flex gap-3">
              {(["png", "jpeg"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`flex-1 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all ${
                    format === f
                      ? "border-[var(--color-gold)] bg-[var(--color-gold-muted)]/30 text-slate-900"
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                >
                  {f === "png" ? "PNG (lossless)" : "JPG (smaller)"}
                </button>
              ))}
            </div>
          </div>

          <button onClick={convert} disabled={processing} className="btn-primary w-full py-3">
            {processing ? (
              <>
                <div className="mr-2 h-4 w-4 rounded-full border-2 border-transparent border-t-current animate-spin" />
                Converting...
              </>
            ) : (
              "Convert to images"
            )}
          </button>
        </>
      )}
    </div>
  );
}
